/**
 * OmniClinicalBridge Physician Verification Components
 * OmniAlera Technologies Holdings Inc.
 * 
 * Critical safety components for clinical decision verification
 * Design Philosophy: AI ASSISTS, PHYSICIAN DECIDES
 */

import React, { useState } from 'react';
import {
  AlertTriangle,
  CheckCircle,
  XCircle,
  Edit3,
  Clock,
  ShieldCheck,
  AlertCircle,
  Stethoscope,
  Info
} from 'lucide-react';
import { ClinicalDecision, VerificationCheckpoint, usePhysicianInLoop } from '../contexts/PhysicianInLoopContext';

interface VerificationDialogProps {
  decision: ClinicalDecision;
  onClose: () => void;
}

/**
 * Main Verification Dialog
 * Requires physician confirmation before critical actions
 */
export const VerificationDialog: React.FC<VerificationDialogProps> = ({
  decision,
  onClose
}) => {
  const { approveDecision, modifyDecision, rejectDecision, deferDecision } = usePhysicianInLoop();
  const [action, setAction] = useState<'approve' | 'modify' | 'reject' | null>(null);
  const [notes, setNotes] = useState('');
  const [modifications, setModifications] = useState('');
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const riskColors = {
    critical: 'bg-red-50 border-red-200 text-red-800',
    high: 'bg-orange-50 border-orange-200 text-orange-800',
    moderate: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    low: 'bg-green-50 border-green-200 text-green-800'
  };
  
  const evidenceColors = {
    A: 'bg-green-100 text-green-800',
    B: 'bg-blue-100 text-blue-800',
    C: 'bg-yellow-100 text-yellow-800'
  };
  
  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      switch (action) {
        case 'approve':
          approveDecision(decision.id, notes || undefined);
          break;
        case 'modify':
          if (!modifications.trim()) {
            alert('Please describe your modifications');
            setIsSubmitting(false);
            return;
          }
          modifyDecision(decision.id, modifications, notes || undefined);
          break;
        case 'reject':
          if (!reason.trim()) {
            alert('Please provide a reason for rejection');
            setIsSubmitting(false);
            return;
          }
          rejectDecision(decision.id, reason);
          break;
      }
      
      onClose();
    } catch (error) {
      console.error('Error processing decision:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      
      {/* Dialog */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className={`px-6 py-4 border-b ${decision.riskLevel === 'critical' ? 'bg-red-50' : 'bg-gray-50'}`}>
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-full ${decision.riskLevel === 'critical' ? 'bg-red-100' : 'bg-blue-100'}`}>
              <ShieldCheck className={`w-6 h-6 ${decision.riskLevel === 'critical' ? 'text-red-600' : 'text-blue-600'}`} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Physician Verification Required</h2>
              <p className="text-sm text-gray-600">Review and verify this clinical recommendation</p>
            </div>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Risk and Evidence Badges */}
          <div className="flex flex-wrap gap-2">
            <span className={`px-3 py-1 rounded-full text-sm font-medium border ${riskColors[decision.riskLevel]}`}>
              {decision.riskLevel.toUpperCase()} RISK
            </span>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${evidenceColors[decision.evidenceLevel]}`}>
              Evidence Level {decision.evidenceLevel}
            </span>
            <span className="px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-700">
              {decision.type}
            </span>
          </div>
          
          {/* AI Recommendation */}
          <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-blue-900 mb-1">AI Recommendation</h3>
                <p className="text-blue-800">{decision.aiRecommendation}</p>
                <p className="text-sm text-blue-600 mt-2">
                  AI Confidence: <span className="font-medium">{decision.aiConfidence}</span>
                </p>
              </div>
            </div>
          </div>
          
          {/* Description */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-2">Clinical Context</h3>
            <p className="text-gray-700">{decision.description}</p>
          </div>
          
          {/* Critical Warning */}
          {decision.riskLevel === 'critical' && (
            <div className="bg-red-50 rounded-xl p-4 border border-red-200">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-red-900 mb-1">Critical Decision Alert</h3>
                  <p className="text-red-800 text-sm">
                    This is a high-stakes clinical decision. Please carefully review all information
                    before proceeding. Your verification is required and will be logged.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Action Selection */}
          <div className="space-y-3">
            <h3 className="font-semibold text-gray-900">Your Decision</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {/* Approve */}
              <button
                onClick={() => setAction('approve')}
                className={`p-4 rounded-xl border-2 transition-all text-left ${
                  action === 'approve'
                    ? 'border-green-500 bg-green-50'
                    : 'border-gray-200 hover:border-green-300 hover:bg-green-50/50'
                }`}
              >
                <CheckCircle className={`w-6 h-6 mb-2 ${action === 'approve' ? 'text-green-600' : 'text-gray-400'}`} />
                <p className="font-semibold text-gray-900">Approve</p>
                <p className="text-sm text-gray-600">Accept AI recommendation</p>
              </button>
              
              {/* Modify */}
              <button
                onClick={() => setAction('modify')}
                className={`p-4 rounded-xl border-2 transition-all text-left ${
                  action === 'modify'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/50'
                }`}
              >
                <Edit3 className={`w-6 h-6 mb-2 ${action === 'modify' ? 'text-blue-600' : 'text-gray-400'}`} />
                <p className="font-semibold text-gray-900">Modify</p>
                <p className="text-sm text-gray-600">Adjust recommendation</p>
              </button>
              
              {/* Reject */}
              <button
                onClick={() => setAction('reject')}
                className={`p-4 rounded-xl border-2 transition-all text-left ${
                  action === 'reject'
                    ? 'border-red-500 bg-red-50'
                    : 'border-gray-200 hover:border-red-300 hover:bg-red-50/50'
                }`}
              >
                <XCircle className={`w-6 h-6 mb-2 ${action === 'reject' ? 'text-red-600' : 'text-gray-400'}`} />
                <p className="font-semibold text-gray-900">Reject</p>
                <p className="text-sm text-gray-600">Override AI recommendation</p>
              </button>
            </div>
          </div>
          
          {/* Action-specific inputs */}
          {action === 'modify' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Describe your modifications <span className="text-red-500">*</span>
              </label>
              <textarea
                value={modifications}
                onChange={(e) => setModifications(e.target.value)}
                placeholder="Explain how you're modifying the recommendation..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                rows={3}
              />
            </div>
          )}
          
          {action === 'reject' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for rejection <span className="text-red-500">*</span>
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Provide clinical reasoning for overriding the AI recommendation..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                rows={3}
              />
            </div>
          )}
          
          {action && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional notes (optional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any additional clinical notes..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                rows={2}
              />
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50 flex items-center justify-between gap-4">
          <button
            onClick={() => deferDecision(decision.id, 'Deferred for later review')}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <Clock className="w-4 h-4" />
            Defer
          </button>
          
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Cancel
            </button>
            
            <button
              onClick={handleSubmit}
              disabled={!action || isSubmitting}
              className={`px-6 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                action === 'approve'
                  ? 'bg-green-600 hover:bg-green-700 text-white'
                  : action === 'modify'
                    ? 'bg-blue-600 hover:bg-blue-700 text-white'
                    : action === 'reject'
                      ? 'bg-red-600 hover:bg-red-700 text-white'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <ShieldCheck className="w-4 h-4" />
                  Verify & Submit
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Checkpoint Alert Component
 * Shows inline verification prompts
 */
export const CheckpointAlert: React.FC<{
  checkpoint: VerificationCheckpoint;
}> = ({ checkpoint }) => {
  const { confirmCheckpoint } = usePhysicianInLoop();
  const [confirmed, setConfirmed] = useState(false);
  
  const handleConfirm = () => {
    setConfirmed(true);
    confirmCheckpoint(checkpoint.id);
  };
  
  if (confirmed || checkpoint.confirmed) return null;
  
  const typeStyles = {
    critical: 'bg-red-50 border-red-300',
    dosing: 'bg-orange-50 border-orange-300',
    interaction: 'bg-yellow-50 border-yellow-300',
    contraindication: 'bg-purple-50 border-purple-300'
  };
  
  const typeIcons = {
    critical: AlertTriangle,
    dosing: Stethoscope,
    interaction: AlertCircle,
    contraindication: XCircle
  };
  
  const Icon = typeIcons[checkpoint.checkpointType];
  
  return (
    <div className={`p-4 rounded-xl border-2 ${typeStyles[checkpoint.checkpointType]} mb-4`}>
      <div className="flex items-start gap-3">
        <Icon className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <p className="font-medium text-gray-900">{checkpoint.message}</p>
          {checkpoint.requiresConfirmation && (
            <button
              onClick={handleConfirm}
              className="mt-3 flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <CheckCircle className="w-4 h-4 text-green-600" />
              <span className="font-medium">I acknowledge and confirm</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Dosing Confirmation Dialog
 * Specific verification for medication dosing decisions
 */
export const DosingConfirmation: React.FC<{
  medication: string;
  dose: string;
  route: string;
  frequency: string;
  indication: string;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ medication, dose, route, frequency, indication, onConfirm, onCancel }) => {
  const [checked, setChecked] = useState({
    dose: false,
    allergies: false,
    interactions: false,
    renalFunction: false
  });
  
  const allChecked = Object.values(checked).every(Boolean);
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onCancel} />
      
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full">
        <div className="px-6 py-4 border-b bg-amber-50">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-amber-100">
              <Stethoscope className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Confirm Dosing</h2>
              <p className="text-sm text-gray-600">Physician verification required</p>
            </div>
          </div>
        </div>
        
        <div className="p-6 space-y-4">
          {/* Medication Details */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-2">{medication}</h3>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div><span className="text-gray-500">Dose:</span> <span className="font-medium">{dose}</span></div>
              <div><span className="text-gray-500">Route:</span> <span className="font-medium">{route}</span></div>
              <div><span className="text-gray-500">Frequency:</span> <span className="font-medium">{frequency}</span></div>
              <div><span className="text-gray-500">Indication:</span> <span className="font-medium">{indication}</span></div>
            </div>
          </div>
          
          {/* Verification Checklist */}
          <div className="space-y-2">
            <p className="font-medium text-gray-900">Verification Checklist:</p>
            
            {[
              { key: 'dose', label: 'Dose is appropriate for indication and patient' },
              { key: 'allergies', label: 'No known drug allergies' },
              { key: 'interactions', label: 'Drug interactions reviewed' },
              { key: 'renalFunction', label: 'Renal/hepatic function considered' }
            ].map(item => (
              <label key={item.key} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                <input
                  type="checkbox"
                  checked={checked[item.key as keyof typeof checked]}
                  onChange={(e) => setChecked(prev => ({ ...prev, [item.key]: e.target.checked }))}
                  className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-gray-700">{item.label}</span>
              </label>
            ))}
          </div>
        </div>
        
        <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={!allChecked}
            className={`px-6 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
              allChecked
                ? 'bg-green-600 hover:bg-green-700 text-white'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            <CheckCircle className="w-4 h-4" />
            Confirm Dosing
          </button>
        </div>
      </div>
    </div>
  );
};

export default VerificationDialog;
